import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Trash2, X, Gift } from "lucide-react";
import { createCheckoutSession, checkReferral } from "../lib/api";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";

const REF_KEY = "flexfeed_ref";

const CartDrawer = () => {
    const { items, total, drawerOpen, setDrawerOpen, removeItem } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [referralCode, setReferralCode] = useState(() => {
        try {
            return localStorage.getItem(REF_KEY) || "";
        } catch {
            return "";
        }
    });
    const [refStatus, setRefStatus] = useState(null); // null | 'valid' | 'invalid' | 'checking'

    const close = () => setDrawerOpen(false);

    const validateRef = async (code) => {
        if (!code.trim()) {
            setRefStatus(null);
            return;
        }
        setRefStatus("checking");
        try {
            const r = await checkReferral(code.trim());
            setRefStatus(r.valid ? "valid" : "invalid");
            if (r.valid) {
                try {
                    localStorage.setItem(REF_KEY, code.trim().toUpperCase());
                } catch {}
            }
        } catch {
            setRefStatus("invalid");
        }
    };

    const handleStartCheckout = async () => {
        if (!user) {
            setDrawerOpen(false);
            navigate("/login", { state: { next: location.pathname } });
            return;
        }
        if (items.length === 0) return;
        setSubmitting(true);
        try {
            const origin_url = window.location.origin;
            const data = await createCheckoutSession(
                items.map((i) => ({ workout_id: i.workout_id, tier_id: i.tier_id })),
                origin_url,
                refStatus === "valid" ? referralCode.trim().toUpperCase() : null
            );
            // Redirect to Stripe Checkout
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
                side="bottom"
                data-testid="cart-drawer"
                className="bg-[#0A0A0A] border-t border-neutral-800 text-white p-0 max-h-[88dvh] mx-auto max-w-[480px] rounded-t-3xl"
            >
                <SheetHeader className="px-5 pt-5 pb-3 flex-row items-center justify-between space-y-0">
                    <SheetTitle className="font-display text-3xl uppercase tracking-wider text-white">
                        Your Plans
                    </SheetTitle>
                    <button
                        data-testid="close-cart"
                        onClick={close}
                        className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center"
                    >
                        <X size={18} />
                    </button>
                </SheetHeader>

                <div className="flex flex-col h-[75dvh]">
                    <div className="flex-1 overflow-y-auto px-5 hide-scrollbar">
                        {items.length === 0 ? (
                            <div data-testid="empty-cart" className="text-center py-16 text-white/60">
                                <p className="font-display text-2xl uppercase">No plans queued</p>
                                <p className="text-sm mt-2">Swipe the feed and grab a subscription.</p>
                            </div>
                        ) : (
                            <>
                                <ul className="space-y-3 py-2">
                                    {items.map((it) => (
                                        <li
                                            key={`${it.workout_id}:${it.tier_id}`}
                                            data-testid={`cart-item-${it.workout_id}`}
                                            className="flex gap-3 bg-[#141414] border border-neutral-800 rounded-xl p-3"
                                        >
                                            <img
                                                src={it.image_url}
                                                alt={it.title}
                                                className="w-20 h-20 object-cover rounded-lg"
                                            />
                                            <div className="flex-1 flex flex-col min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="text-[10px] uppercase tracking-widest text-[#E1FF01]">
                                                        {it.tier_name} · / {it.period}
                                                    </span>
                                                    <button
                                                        data-testid={`remove-${it.workout_id}`}
                                                        onClick={() => removeItem(it.workout_id, it.tier_id)}
                                                        className="text-white/50 hover:text-white"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <p className="font-semibold text-sm truncate mt-0.5">{it.title}</p>
                                                <div className="mt-auto flex items-center justify-between">
                                                    <span className="text-[10px] uppercase tracking-widest text-white/50">
                                                        Auto-renews
                                                    </span>
                                                    <span className="font-display text-lg">
                                                        ${it.price.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                {/* Referral input */}
                                <div className="mt-4 mb-2 bg-[#141414] border border-neutral-800 rounded-xl p-3">
                                    <label className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1">
                                        <Gift size={11} className="text-[#E1FF01]" />
                                        Referral code (optional)
                                    </label>
                                    <input
                                        data-testid="referral-input"
                                        value={referralCode}
                                        onChange={(e) => {
                                            setReferralCode(e.target.value.toUpperCase());
                                            setRefStatus(null);
                                        }}
                                        onBlur={(e) => validateRef(e.target.value)}
                                        placeholder="FX••••••"
                                        className="mt-1 w-full bg-[#0A0A0A] border border-neutral-800 rounded-md px-3 py-2 text-sm tracking-widest focus:outline-none focus:border-[#E1FF01]"
                                    />
                                    {refStatus === "valid" && (
                                        <p className="text-[10px] text-[#E1FF01] mt-1.5 uppercase tracking-widest">
                                            ✓ Code applied — they'll get a free week
                                        </p>
                                    )}
                                    {refStatus === "invalid" && (
                                        <p className="text-[10px] text-[#FF3B30] mt-1.5 uppercase tracking-widest">
                                            Invalid code
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {items.length > 0 && (
                        <div className="px-5 pt-3 pb-6 border-t border-neutral-800 bg-[#0A0A0A]">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs uppercase tracking-widest text-white/60">
                                    Charge today
                                </span>
                                <span data-testid="cart-total" className="font-display text-3xl">
                                    ${total.toFixed(2)}
                                </span>
                            </div>
                            <button
                                data-testid="proceed-checkout"
                                disabled={submitting}
                                onClick={handleStartCheckout}
                                className="w-full bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-xl uppercase tracking-wider py-3.5 rounded-md transition active:scale-[0.98] disabled:opacity-60"
                            >
                                {submitting
                                    ? "Redirecting…"
                                    : user
                                    ? "Pay with Stripe"
                                    : "Sign in to Subscribe"}
                            </button>
                            <p className="text-[10px] text-white/40 mt-2 text-center uppercase tracking-widest">
                                Secured by Stripe · Test mode
                            </p>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default CartDrawer;
